import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Search,
  Star,
  StarOff,
  ChevronRight,
  ChevronDown,
  Loader2,
  Heart,
  Trash2,
  FolderTree,
  List,
  ExternalLink,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useCpvBatch } from '@/hooks/useCpvBatch';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';

interface CpvCode {
  code: string;
  description: string;
  category: string;
  division: string | null;
}

interface CpvTreeNode {
  code: string;
  description: string;
  category: string;
  children: CpvTreeNode[];
  level: number;
}

export default function CpvSearchPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { getCpvData, requestCpv } = useCpvBatch();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<CpvCode[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [favoritesData, setFavoritesData] = useState<CpvCode[]>([]);
  const [loadingFavorites, setLoadingFavorites] = useState(true);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'search' | 'favorites'>('search');
  const [viewMode, setViewMode] = useState<'tree' | 'list'>('tree');
  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set());
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Load favorites on mount
  useEffect(() => {
    if (!user) return;
    loadFavorites();
  }, [user]);

  const loadFavorites = async () => {
    if (!user) return;
    setLoadingFavorites(true);
    try {
      const { data, error } = await supabase
        .from('cpv_favorites')
        .select('cpv_code')
        .eq('user_id', user.id);

      if (error) throw error;
      const codes = new Set(data?.map(d => d.cpv_code) || []);
      setFavorites(codes);

      // Load full data for favorites with translations
      if (codes.size > 0) {
        const codesArr = Array.from(codes);
        const currentLang = i18n.language?.substring(0, 2) || 'en';

        // cpv_codes uses codes without check digit (e.g. 45110000)
        // cpv_translations uses codes with check digit (e.g. 45110000-1)
        // Build OR filter to match translations by prefix
        const translationFilter = codesArr.map(c => `code.ilike.${c}%`).join(',');

        const [{ data: cpvData }, { data: translations }] = await Promise.all([
          supabase.from('cpv_codes').select('code, description, category, division').in('code', codesArr),
          supabase.from('cpv_translations').select('code, description').eq('language', currentLang).or(translationFilter),
        ]);

        // Map translations back to the short code format used in cpv_codes
        const translationMap = new Map<string, string>();
        (translations || []).forEach(t => {
          // Strip check digit: "45110000-1" -> "45110000"
          const shortCode = t.code.replace(/-\d+$/, '');
          translationMap.set(shortCode, t.description);
          translationMap.set(t.code, t.description); // also keep full code
        });

        // Also fetch category translations for divisions (parallel)
        const divisions = new Set((cpvData || []).map(c => c.division).filter(Boolean) as string[]);
        const categoryMap = new Map<string, string>();
        if (divisions.size > 0 && currentLang !== 'en') {
          const catPromises = Array.from(divisions).map(div =>
            supabase
              .from('cpv_translations')
              .select('code, description')
              .eq('language', currentLang)
              .ilike('code', `${div}000000%`)
              .limit(1)
              .then(({ data }) => {
                if (data && data.length > 0) categoryMap.set(div, data[0].description);
              })
          );
          await Promise.all(catPromises);
        }

        const enriched: CpvCode[] = (cpvData || []).map(cpv => ({
          ...cpv,
          description: translationMap.get(cpv.code) || cpv.description,
          category: (cpv.division && categoryMap.get(cpv.division)) || cpv.category,
        }));
        setFavoritesData(enriched);
      }
    } catch (e) {
      console.error('Error loading favorites:', e);
    } finally {
      setLoadingFavorites(false);
    }
  };

  const searchCpv = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    setIsLoading(true);
    try {
      const currentLang = i18n.language?.substring(0, 2) || 'en';

      // Search by code directly
      const codeSearchPromise = query.match(/^\d/)
        ? supabase.from('cpv_codes').select('code, description, category, division').ilike('code', `%${query}%`).limit(50)
        : Promise.resolve({ data: [] as CpvCode[] });

      // Search translations in current language
      const translationSearchPromise = supabase
        .from('cpv_translations')
        .select('code, description')
        .eq('language', currentLang)
        .ilike('description', `%${query}%`)
        .limit(80);

      const [{ data: codeData }, { data: translationHits, error: trError }] = await Promise.all([codeSearchPromise, translationSearchPromise]);

      if (trError) console.error('Translation search error:', trError);

      const matchedCodes = new Set((translationHits || []).map(t => t.code));
      const translationMap = new Map((translationHits || []).map(t => [t.code, t.description]));

      (codeData || []).forEach(c => matchedCodes.add(c.code));

      if (matchedCodes.size === 0) {
        // Fallback: search cpv_codes English descriptions
        const { data: fallback } = await supabase
          .from('cpv_codes')
          .select('code, description, category, division')
          .or(`description.ilike.%${query}%,category.ilike.%${query}%`)
          .order('code')
          .limit(100);
        setSearchResults(fallback || []);
        setIsLoading(false);
        return;
      }

      // Fetch full cpv_codes data for matched codes
      const allCodes = Array.from(matchedCodes);
      const { data: cpvData, error: cpvError } = await supabase
        .from('cpv_codes')
        .select('code, description, category, division')
        .in('code', allCodes)
        .order('code')
        .limit(100);

      if (cpvError) throw cpvError;

      // Collect unique division prefixes (e.g. "45", "48") to translate categories
      const divPrefixes = new Set<string>();
      (cpvData || []).forEach(cpv => {
        if (cpv.division) divPrefixes.add(cpv.division);
      });

      const categoryMap = new Map<string, string>();
      if (divPrefixes.size > 0 && currentLang !== 'en') {
        // Batch fetch category translations using ilike (codes have check digits like 45000000-7)
        const catPromises = Array.from(divPrefixes).map(div =>
          supabase
            .from('cpv_translations')
            .select('code, description')
            .eq('language', currentLang)
            .ilike('code', `${div}000000%`)
            .limit(1)
            .then(({ data }) => {
              if (data && data.length > 0) categoryMap.set(div, data[0].description);
            })
        );
        await Promise.all(catPromises);
      }

      // Override descriptions and categories with translated ones
      const results: CpvCode[] = (cpvData || []).map(cpv => ({
        ...cpv,
        description: translationMap.get(cpv.code) || cpv.description,
        category: (cpv.division && categoryMap.get(cpv.division)) || cpv.category,
      }));

      setSearchResults(results);
    } catch (e) {
      console.error('Error searching CPV:', e);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchCpv(searchQuery), 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchQuery, searchCpv]);

  const toggleFavorite = async (code: string) => {
    if (!user) {
      toast.error(t('cpv.loginRequired'));
      return;
    }

    const isFav = favorites.has(code);
    // Optimistic update
    setFavorites(prev => {
      const next = new Set(prev);
      if (isFav) next.delete(code); else next.add(code);
      return next;
    });

    try {
      if (isFav) {
        const { error } = await supabase
          .from('cpv_favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('cpv_code', code);
        if (error) throw error;
        setFavoritesData(prev => prev.filter(f => f.code !== code));
        toast.success(t('cpv.removedFromFavorites'));
      } else {
        const { error } = await supabase
          .from('cpv_favorites')
          .insert({ user_id: user.id, cpv_code: code });
        if (error) throw error;
        // Add to favorites data
        const item = searchResults.find(r => r.code === code);
        if (item) setFavoritesData(prev => [...prev, item]);
        toast.success(t('cpv.addedToFavorites'));
      }
    } catch (e) {
      // Revert
      setFavorites(prev => {
        const next = new Set(prev);
        if (isFav) next.add(code); else next.delete(code);
        return next;
      });
      toast.error(t('cpv.favoriteError'));
    }
  };

  // Build tree structure from flat list
  const buildTree = (items: CpvCode[]): CpvTreeNode[] => {
    // Group by division (first 2 digits)
    const divisions = new Map<string, CpvCode[]>();
    items.forEach(item => {
      const div = item.code.substring(0, 2);
      if (!divisions.has(div)) divisions.set(div, []);
      divisions.get(div)!.push(item);
    });

    const tree: CpvTreeNode[] = [];
    divisions.forEach((codes, div) => {
      // Find the best division-level description: prefer the broadest code's description
      const divItem = codes.find(c => c.code.replace(/-\d$/, '').replace(/0+$/, '') === div) || codes[0];
      const divDescription = divItem?.category || divItem?.description || `${t('cpv.division')} ${div}`;

      // If only one code in this division, show it directly
      if (codes.length === 1) {
        tree.push({
          code: codes[0].code,
          description: codes[0].description,
          category: codes[0].category,
          children: [],
          level: 0,
        });
        return;
      }

      // Group by group (first 5 digits for more granularity)
      const groups = new Map<string, CpvCode[]>();
      codes.forEach(code => {
        const grp = code.code.substring(0, 5).replace(/-.*$/, '');
        if (!groups.has(grp)) groups.set(grp, []);
        groups.get(grp)!.push(code);
      });

      const groupNodes: CpvTreeNode[] = [];
      groups.forEach((groupCodes) => {
        // Show each code as a leaf directly
        groupCodes.forEach(c => {
          groupNodes.push({
            code: c.code,
            description: c.description,
            category: c.category,
            children: [],
            level: 1,
          });
        });
      });

      tree.push({
        code: div + '000000',
        description: divDescription,
        category: divDescription,
        children: groupNodes,
        level: 0,
      });
    });

    return tree.sort((a, b) => a.code.localeCompare(b.code));
  };

  const toggleExpand = (nodeCode: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeCode)) next.delete(nodeCode); else next.add(nodeCode);
      return next;
    });
  };

  const renderTreeNode = (node: CpvTreeNode) => {
    const hasChildren = node.children.length > 0;
    const isExpanded = expandedNodes.has(node.code);
    const isFav = favorites.has(node.code);
    const isLeaf = !hasChildren;

    return (
      <div key={node.code}>
        <div
          className={cn(
            "flex items-center gap-2 py-2 px-3 rounded-lg transition-colors group cursor-pointer",
            "hover:bg-muted/50",
            node.level === 0 && "font-semibold",
            node.level === 1 && "ml-4",
            node.level === 2 && "ml-8",
          )}
          onClick={() => hasChildren ? toggleExpand(node.code) : undefined}
        >
          {hasChildren ? (
            <button onClick={(e) => { e.stopPropagation(); toggleExpand(node.code); }} className="p-0.5">
              {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
            </button>
          ) : (
            <span className="w-5" />
          )}

          <Badge variant="outline" className="font-mono text-xs shrink-0">
            {isLeaf ? node.code : node.code.substring(0, 2) + 'xxx'}
          </Badge>

          <span className="flex-1 text-sm" title={node.description}>
            {node.description}
          </span>

          {isLeaf && (
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => { e.stopPropagation(); toggleFavorite(node.code); }}
                  className={cn(
                    "p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity",
                    isFav && "opacity-100"
                  )}
                >
                  <Star className={cn("w-4 h-4", isFav ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground")} />
                </button>
              </TooltipTrigger>
              <TooltipContent>{isFav ? t('cpv.removeFromFavorites') : t('cpv.addToFavorites')}</TooltipContent>
            </Tooltip>
          )}
        </div>

        {hasChildren && isExpanded && (
          <div>
            {node.children.map(child => renderTreeNode(child))}
          </div>
        )}
      </div>
    );
  };

  const renderListItem = (item: CpvCode) => {
    const isFav = favorites.has(item.code);
    return (
      <div
        key={item.code}
        className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-muted/50 transition-colors group"
      >
        <Badge variant="outline" className="font-mono text-xs shrink-0">
          {item.code}
        </Badge>
        <div className="flex-1 min-w-0">
          <p className="text-sm">{item.description}</p>
          <p className="text-xs text-muted-foreground">{item.category}</p>
        </div>
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <button
              onClick={() => toggleFavorite(item.code)}
              className={cn(
                "p-1.5 rounded-md transition-all",
                isFav
                  ? "text-yellow-500 hover:text-yellow-600"
                  : "text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-foreground"
              )}
            >
              <Star className={cn("w-4 h-4", isFav && "fill-current")} />
            </button>
          </TooltipTrigger>
          <TooltipContent>{isFav ? t('cpv.removeFromFavorites') : t('cpv.addToFavorites')}</TooltipContent>
        </Tooltip>
      </div>
    );
  };

  const tree = buildTree(searchResults);

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('cpv.pageTitle')}</h1>
          <p className="text-muted-foreground mt-1">
            {t('cpv.pageSubtitle')}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1 w-fit">
          <button
            onClick={() => setActiveTab('search')}
            className={cn(
              "px-4 py-2 rounded-md text-sm font-medium transition-colors",
              activeTab === 'search'
                ? "bg-background shadow-sm text-foreground"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <Search className="w-4 h-4 inline mr-2" />
            {t('cpv.searchTab')}
          </button>
          <button
            onClick={() => setActiveTab('favorites')}
            className={cn(
              "px-4 py-2 rounded-md text-sm font-medium transition-colors",
              activeTab === 'favorites'
                ? "bg-background shadow-sm text-foreground"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <Heart className="w-4 h-4 inline mr-2" />
            {t('cpv.favoritesTab')}
            {favorites.size > 0 && (
              <Badge variant="secondary" className="ml-2 text-xs">{favorites.size}</Badge>
            )}
          </button>
        </div>

        {activeTab === 'search' && (
          <div className="space-y-4">
            {/* Search bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('cpv.searchInputPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
              {isLoading && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
              )}
            </div>

            {/* View mode toggle */}
            {searchResults.length > 0 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {t('cpv.resultsFound', { count: searchResults.length })}
                </p>
                <div className="flex items-center gap-1 bg-muted/50 rounded-md p-0.5">
                  <button
                    onClick={() => setViewMode('tree')}
                    className={cn(
                      "p-1.5 rounded transition-colors",
                      viewMode === 'tree' ? "bg-background shadow-sm" : "text-muted-foreground"
                    )}
                    title={t('cpv.treeView')}
                  >
                    <FolderTree className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={cn(
                      "p-1.5 rounded transition-colors",
                      viewMode === 'list' ? "bg-background shadow-sm" : "text-muted-foreground"
                    )}
                    title={t('cpv.listView')}
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Results */}
            {searchResults.length > 0 ? (
              <div className="border rounded-lg bg-card">
                <ScrollArea className="h-[60vh]">
                  <div className="p-2">
                    {viewMode === 'tree'
                      ? tree.map(node => renderTreeNode(node))
                      : searchResults.map(item => renderListItem(item))
                    }
                  </div>
                </ScrollArea>
              </div>
            ) : searchQuery.length >= 2 && !isLoading ? (
              <div className="text-center py-12 text-muted-foreground">
                <Search className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p>{t('cpv.noResultsFor', { query: searchQuery })}</p>
              </div>
            ) : searchQuery.length < 2 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FolderTree className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p>{t('cpv.typeMinChars')}</p>
              </div>
            ) : null}
          </div>
        )}

        {activeTab === 'favorites' && (
          <div className="space-y-4">
            {/* Multi-select action bar */}
            {favoritesData.length > 0 && (
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={selectedCodes.size === favoritesData.length && favoritesData.length > 0}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedCodes(new Set(favoritesData.map(f => f.code)));
                    } else {
                      setSelectedCodes(new Set());
                    }
                  }}
                />
                <span className="text-sm text-muted-foreground">
                  {selectedCodes.size > 0 
                    ? t('cpv.selected', { count: selectedCodes.size })
                    : t('cpv.selectAll')
                  }
                </span>
                {selectedCodes.size > 0 && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedCodes(new Set())}
                    >
                      {t('cpv.deselectAll')}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => navigate(`/search?cpv=${Array.from(selectedCodes).join(',')}`)}
                      className="ml-auto"
                    >
                      <Search className="w-4 h-4 mr-1" />
                      {t('cpv.searchSelectedCpv', { count: selectedCodes.size })}
                    </Button>
                  </>
                )}
              </div>
            )}

            {loadingFavorites ? (
              <div className="text-center py-12">
                <Loader2 className="w-6 h-6 mx-auto animate-spin text-muted-foreground" />
              </div>
            ) : favoritesData.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Heart className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p>{t('cpv.noFavorites')}</p>
                <p className="text-sm mt-1">{t('cpv.noFavoritesHint')}</p>
              </div>
            ) : (
              <div className="border rounded-lg bg-card">
                <ScrollArea className="h-[60vh]">
                  <div className="p-2 space-y-0.5">
                    {favoritesData
                      .sort((a, b) => a.code.localeCompare(b.code))
                      .map(item => {
                        const isSelected = selectedCodes.has(item.code);
                        return (
                          <div
                            key={item.code}
                            className={cn(
                              "flex items-center gap-3 py-3 px-3 rounded-lg hover:bg-muted/50 transition-colors group cursor-pointer",
                              isSelected && "bg-primary/5"
                            )}
                            onClick={() => {
                              setSelectedCodes(prev => {
                                const next = new Set(prev);
                                if (next.has(item.code)) next.delete(item.code);
                                else next.add(item.code);
                                return next;
                              });
                            }}
                          >
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => {
                                setSelectedCodes(prev => {
                                  const next = new Set(prev);
                                  if (next.has(item.code)) next.delete(item.code);
                                  else next.add(item.code);
                                  return next;
                                });
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <Badge variant="outline" className="font-mono text-xs shrink-0">
                              {item.code}
                            </Badge>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm">{item.description}</p>
                              <p className="text-xs text-muted-foreground">{item.category}</p>
                              {item.division && (
                                <p className="text-xs text-muted-foreground/70">{t('cpv.division')}: {item.division}</p>
                              )}
                            </div>
                            <Tooltip delayDuration={200}>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={(e) => { e.stopPropagation(); navigate(`/search?cpv=${item.code}`); }}
                                  className="p-1.5 rounded-md text-primary opacity-0 group-hover:opacity-100 transition-opacity hover:bg-primary/10"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>{t('cpv.searchWithCpv')}</TooltipContent>
                            </Tooltip>
                            <Tooltip delayDuration={200}>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={(e) => { e.stopPropagation(); toggleFavorite(item.code); }}
                                  className="p-1.5 rounded-md text-destructive opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/10"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>{t('cpv.removeFromFavorites')}</TooltipContent>
                            </Tooltip>
                          </div>
                        );
                      })
                    }
                  </div>
                </ScrollArea>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
